import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { Bundle, ChargeItemDefinition, SupplyDelivery } from 'fhir/r4';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatStepper, MatStepperModule, StepperOrientation } from '@angular/material/stepper';
import { BreakpointObserver } from '@angular/cdk/layout';
import { forkJoin, map, Observable, startWith, take, switchMap, of, firstValueFrom } from 'rxjs';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { DynamicFormsV2Component } from '../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { CodeableConceptField, formMetaData, GroupField, IndividualField, SingleCodeField } from '../shared/dynamic-forms.interface2';
import { ErrorService } from '../shared/error.service';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { UtilityService } from '../shared/utility.service';
import { StateService } from '../shared/state.service';
import { AuthService } from '../shared/auth/auth.service';

type FormFields = IndividualField | GroupField | SingleCodeField | CodeableConceptField;

@Component({
    selector: 'app-add-supply-delivery',
    standalone: true,
    imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule, MatStepperModule, DynamicFormsV2Component],
    templateUrl: './add-supply-delivery.component.html',
    styleUrl: './add-supply-delivery.component.scss',
})
export class AddSupplyDeliveryComponent implements OnInit {
    private http = inject(HttpClient);
    private backendUrl = inject(backendEndPointToken);
    private canonicalTerminologyUrl = inject(terminologyCanonicalUrlBaseToken);
    private formFieldsDataService = inject(FormFieldsSelectDataService);
    private errorService = inject(ErrorService);
    private dialog = inject(MatDialog);
    private dialogRef = inject(MatDialogRef<AddSupplyDeliveryComponent>, { optional: true });
    private utilityService = inject(UtilityService);
    private stateService = inject(StateService);
    private authService = inject(AuthService);
    private breakpointObserver = inject(BreakpointObserver);

    readonly stepperOrientation$: Observable<StepperOrientation> = this.breakpointObserver
        .observe('(max-width: 767px)')
        .pipe(
            map(({ matches }) => (matches ? 'vertical' : 'horizontal') as StepperOrientation),
            startWith('horizontal' as StepperOrientation)
        );

    private readonly supplyDeliveryExtensionUrls = {
        totalAmount: this.canonicalTerminologyUrl + '/StructureDefinition/totalAmount',
        totalRemaining: this.canonicalTerminologyUrl + '/StructureDefinition/totalRemaining',
        packItemRemaining: this.canonicalTerminologyUrl + '/StructureDefinition/packItemRemaining',
        packageDetails: this.canonicalTerminologyUrl + '/StructureDefinition/packageDetails',
        totalCostPrice: this.canonicalTerminologyUrl + '/StructureDefinition/totalCostPrice',
        totalRemainingChanges: this.canonicalTerminologyUrl + '/StructureDefinition/totalRemainingChanges',
        lowStockThreshold: this.canonicalTerminologyUrl + '/StructureDefinition/lowStockThreshold',
        lowStockAlertsEnabled: this.canonicalTerminologyUrl + '/StructureDefinition/lowStockAlertsEnabled',
    } as const;

    step1Meta: formMetaData = {
        formName: 'Material details',
        formDescription: 'Select the item and its dispensable unit/quantity.',
        submitText: 'Next',
    };
    step2Meta: formMetaData = {
        formName: 'Packaging',
        formDescription: 'Optional package/container details for the delivery.',
        submitText: 'Next',
    };
    step3Meta: formMetaData = {
        formName: 'Cost price',
        formDescription: 'Enter the total cost price for the supplied dispensable quantity.',
        submitText: 'Next',
    };

    step1Fields: FormFields[] = [];
    step2Fields: FormFields[] = [];
    step3Fields: FormFields[] = [];

    private draft: any = {};

    ngOnInit(): void {
        console.log('Initializing AddSupplyDeliveryComponent...');
        forkJoin({
            substanceOptions: this.formFieldsDataService.getFormFieldSelectData('supplyDelivery', 'substanceCode'),
        }).pipe(take(1)).subscribe({
            next: ({ substanceOptions }) => {
                const { step1, step2, step3 } = this.buildStepFields(substanceOptions || []);
                this.step1Fields = step1;
                this.step2Fields = step2;
                this.step3Fields = step3;
            },
            error: () => {
                // If backend options fail, still show the form with free-text fallback.
                const { step1, step2, step3 } = this.buildStepFields([]);
                this.step1Fields = step1;
                this.step2Fields = step2;
                this.step3Fields = step3;
            }
        });
    }

    private buildStepFields(substanceOptions: any[]): { step1: FormFields[]; step2: FormFields[]; step3: FormFields[] } {
        const step1: FormFields[] = [
            <CodeableConceptField>{
                generalProperties: {
                    fieldApiName: 'substance',
                    fieldName: 'Item Name',
                    fieldLabel: 'Name of supplied/delivered item',
                    fieldHint: 'Select the item being supplied/delivered. You can also enter a custom name.',
                    auth: { read: 'all', write: 'admin' },
                    isArray: false,
                    isGroup: false,
                    fieldType: 'CodeableConceptField',
                    allowedOthers: true,
                    //required validation config
                    validations: [
                        {
                            name: 'required',
                            type: 'default',
                            isFunction: false,
                        }
                    ]
                },
                data: substanceOptions,
            },
            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'dispensableQuantity',
                    fieldName: 'Dispensable Quantity',
                    fieldLabel: 'Dispensable Quantity',
                    fieldPlaceholder: '1',
                    fieldHint: 'This quantity is in the dispensable unit (the unit the item will be used/dispensed in).',
                    auth: { read: 'all', write: 'admin' },
                    isArray: false,
                    isGroup: false,
                    inputType: 'number',
                    value: 1,
                    fieldType: 'IndividualField',
                    validations: [
                        {
                            name: 'required',
                            type: 'default',
                            isFunction: false,
                        }
                    ]
                },
            },
            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'dispensableUnit',
                    fieldName: 'Dispensable Unit',
                    fieldLabel: 'Dispensable Unit',
                    fieldPlaceholder: 'e.g., pcs, mL, tablet',
                    fieldHint: 'This is the unit you will dispense/use (NOT the package name/unit).',
                    auth: { read: 'all', write: 'admin' },
                    isArray: false,
                    isGroup: false,
                    inputType: 'text',
                    fieldType: 'IndividualField',
                    validations: [
                        {
                            name: 'required',
                            type: 'default',
                            isFunction: false,
                        }
                    ]
                },
            },
            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'lowStockThreshold',
                    fieldName: 'Low-stock Threshold',
                    fieldLabel: 'Low-stock Threshold',
                    fieldPlaceholder: 'e.g., 10',
                    fieldHint: 'When remaining units fall to or below this number, alert inventory managers.',
                    auth: { read: 'all', write: 'admin' },
                    isArray: false,
                    isGroup: false,
                    inputType: 'number',
                    fieldType: 'IndividualField',
                },
            },
            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'lowStockAlertsEnabled',
                    fieldName: 'Send Low-stock Alerts',
                    fieldLabel: 'Send Low-stock Alerts',
                    fieldHint: 'Toggle to automatically send alerts when the threshold is breached.',
                    auth: { read: 'all', write: 'admin' },
                    isArray: false,
                    isGroup: false,
                    inputType: 'toggle',
                    fieldType: 'IndividualField',
                },
            },
            // <IndividualField>{
            //     generalProperties: {
            //         fieldApiName: 'quantityRemaining',
            //         fieldName: 'Quantity Remaining',
            //         fieldLabel: 'Quantity Remaining (dispensable unit)',
            //         fieldPlaceholder: '1',
            //         fieldHint: 'Stock remaining in the same dispensable unit.',
            //         auth: { read: 'all', write: 'admin' },
            //         isArray: false,
            //         isGroup: false,
            //         inputType: 'number',
            //         value: 1,

            //         fieldType: 'IndividualField',
            //     },
            // },
            <SingleCodeField>{
                generalProperties: {
                    fieldApiName: 'status',
                    fieldName: 'Status',
                    fieldLabel: 'Status',
                    fieldType: 'CodeField',
                    auth: { read: 'all', write: 'admin' },
                    isArray: false,
                    isGroup: false,
                    value: 'completed',
                    isHidden: true,
                },

                data: ['in-progress', 'completed', 'abandoned', 'entered-in-error'],
            },
        ];

        const step2: FormFields[] = [
            // <IndividualField>{
            //     generalProperties: {
            //         fieldApiName: 'packagesRemaining',
            //         fieldName: 'Packages Remaining',
            //         fieldLabel: 'Packages Remaining',
            //         fieldPlaceholder: 'e.g., 2',
            //         fieldHint: 'Optional: remaining packages/boxes/bottles.',
            //         auth: { read: 'all', write: 'admin' },
            //         isArray: false,
            //         isGroup: false,
            //         inputType: 'number',
            //         fieldType: 'IndividualField',
            //     },
            // },
            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'packageName',
                    fieldName: 'Package Type',
                    fieldLabel: 'Package Type',
                    fieldPlaceholder: 'e.g., box, bottle, pack',
                    fieldHint: 'Optional: the package/container name in which the delivery comes.',
                    auth: { read: 'all', write: 'admin' },
                    isArray: false,
                    isGroup: false,
                    inputType: 'text',
                    fieldType: 'IndividualField',
                },
            },
            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'packagesReceived',
                    fieldName: 'Quantity of Packages Received',
                    fieldLabel: 'Quantity of Packages Received',
                    fieldPlaceholder: 'e.g., 20',
                    fieldHint: 'Optional: how many packages/boxes/bottles were received in this delivery.',
                    auth: { read: 'all', write: 'admin' },
                    isArray: false,
                    isGroup: false,
                    inputType: 'number',
                    fieldType: 'IndividualField',
                },
            },
            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'packageSize',
                    fieldName: 'Quantity Per Package',
                    fieldLabel: 'Quantity Per Package (dispensable units per package)',
                    fieldPlaceholder: 'e.g., 100',
                    fieldHint: 'Optional: how many dispensable units are in ONE package (e.g., bottle contains 100 mL).',
                    auth: { read: 'all', write: 'admin' },
                    isArray: false,
                    isGroup: false,
                    inputType: 'number',
                    fieldType: 'IndividualField',
                },
            },
        ];

        const step3: FormFields[] = [
            <IndividualField>{
                generalProperties: {
                    fieldApiName: 'totalCostPrice',
                    fieldName: 'Total Cost Price',
                    fieldLabel: 'Total cost price (for dispensable quantity)',
                    fieldPlaceholder: 'e.g., 5000',
                    fieldHint: 'Enter the TOTAL cost price for the entire dispensable quantity entered in step 1 (not per unit).',
                    auth: { read: 'all', write: 'admin' },
                    isArray: false,
                    isGroup: false,
                    inputType: 'number',
                    fieldType: 'IndividualField',
                    validations: [
                        {
                            name: 'required',
                            type: 'default',
                            isFunction: false,
                        }
                    ]
                },
            },
        ];

        return { step1, step2, step3 };
    }

    private parseCodeableConceptSelection(selection: any): { code?: string; display?: string; system?: string; text?: string } {
        if (selection === null || selection === undefined) {
            return {};
        }

        if (typeof selection === 'string') {
            const raw = selection.trim();
            if (!raw) return {};
            if (raw.includes('$#$')) {
                const [code, display, system] = raw.split('$#$');
                return {
                    code: (code ?? '').trim() || undefined,
                    display: (display ?? '').trim() || undefined,
                    system: (system ?? '').trim() || undefined,
                    text: ((display ?? '').trim() || (code ?? '').trim()) || undefined,
                };
            }
            // Free-text fallback
            return { code: raw, display: raw, text: raw };
        }

        if (typeof selection === 'object') {
            const code = (selection.code ?? selection.coding?.[0]?.code ?? '').toString().trim();
            const display = (selection.display ?? selection.coding?.[0]?.display ?? selection.text ?? '').toString().trim();
            const system = (selection.system ?? selection.coding?.[0]?.system ?? '').toString().trim();
            const text = (selection.text ?? display ?? code).toString().trim();
            return {
                code: code || undefined,
                display: display || undefined,
                system: system || undefined,
                text: text || undefined,
            };
        }

        return {};
    }
    @ViewChild(MatStepper) private stepper!: MatStepper;

    goNext(): void {
        this.stepper.next();
    }

    goTo(stepIndex: number): void {
        this.stepper.selectedIndex = stepIndex; // 0-based
    }
    private toNumberOrUndefined(val: any): number | undefined {
        if (val === '' || val === null || val === undefined) return undefined;
        const n = Number(val);
        return Number.isFinite(n) && n >= 0 ? n : undefined;
    }
    private toBooleanOrUndefined(val: any): boolean | undefined {
        if (val === '' || val === null || val === undefined) return undefined;
        if (typeof val === 'boolean') return val;
        if (typeof val === 'number') {
            if (val === 1) return true;
            if (val === 0) return false;
        }
        if (typeof val === 'string') {
            const normalized = val.trim().toLowerCase();
            if (!normalized) return undefined;
            if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
            if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        }
        return undefined;
    }
    isStep1ValuesValid() {
        const dispensableQuantity = this.toNumberOrUndefined(this.draft?.dispensableQuantity);
        const dispensableUnit = (this.draft?.dispensableUnit ?? '').toString().trim();
        const substance = this.parseCodeableConceptSelection(this.draft?.substance);
        const name = (substance.text ?? '').toString().trim();
        return !!(name && dispensableUnit && dispensableQuantity !== undefined);
    }
    // isStep2ValuesValid(){

    // }

    onStep1Next(formData: any): void {
        this.draft = { ...this.draft, ...(formData || {}) };

        // Default quantityRemaining to dispensableQuantity if not provided
        if (this.draft.quantityRemaining === undefined || this.draft.quantityRemaining === null || this.draft.quantityRemaining === '') {
            this.draft.quantityRemaining = this.draft.dispensableQuantity;
        }
        if (this.isStep1ValuesValid()) {
            this.goNext()
        }
    }

    onStep2Next(formData: any): void {
        this.draft = { ...this.draft, ...(formData || {}) };

        this.goNext()
    }

    onStep3Next(formData: any): void {
        this.draft = { ...this.draft, ...(formData || {}) };
        this.saveMaterial();
    }

    async saveMaterial(): Promise<void> {
        const formData = this.draft || {};

        const substance = this.parseCodeableConceptSelection(formData?.substance);
        const name = (substance.text ?? '').toString().trim();
        if (!name) {
            this.errorService.openandCloseError('Item name is required');
            return;
        }
        //if either quantity or unit is absent, show error
        if ((formData?.dispensableQuantity === undefined || formData?.dispensableQuantity === null || formData?.dispensableQuantity === '') ||
            (formData?.dispensableUnit === undefined || formData?.dispensableUnit === null || formData?.dispensableUnit === '')) {
            this.errorService.openandCloseError('Dispensable quantity or unit must be provided');
            return;
        }

        const dispensableUnit = (formData?.dispensableUnit ?? '').toString().trim();
        const dispensableQuantity = this.toNumberOrUndefined(formData?.dispensableQuantity);
        const quantityRemaining = this.toNumberOrUndefined(formData?.quantityRemaining);
        const packagesRemaining = this.toNumberOrUndefined(formData?.packagesRemaining);
        const packageName = (formData?.packageName ?? '').toString().trim();
        const packagesReceived = this.toNumberOrUndefined(formData?.packagesReceived);
        const packageSize = this.toNumberOrUndefined(formData?.packageSize);
        const totalCostPrice = this.toNumberOrUndefined(formData?.totalCostPrice);
        const lowStockThreshold = this.toNumberOrUndefined(formData?.lowStockThreshold);
        const lowStockAlertsEnabled = this.toBooleanOrUndefined(formData?.lowStockAlertsEnabled);

        if (totalCostPrice === undefined) {
            this.errorService.openandCloseError('Total cost price is required');
            return;
        }

        const supplyCode = String(substance.code ?? name).toLowerCase();

        try {
            const bundle = await firstValueFrom(this.http.get<Bundle>(`${this.backendUrl}/SupplyDelivery?_tag:text=${encodeURIComponent(supplyCode)}&status=in-progress,completed&_count=1`));
            const hasActiveDuplicate = (bundle.entry?.length || 0) > 0;
            if (hasActiveDuplicate) {
                const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                    data: {
                        title: 'Duplicate Product Code',
                        messagesArray: [
                            `An active supply stock batch with this product code already exists.`,
                            `Are you sure you want to add a new batch? Usually, you should just adjust the stock of the existing batch.`
                        ]
                    }
                });
                const proceed = await firstValueFrom(dialogRef.afterClosed());
                if (!proceed) return;
            }
        } catch (e) {
            console.error('Duplicate check failed', e);
        }

        const quantityValue = dispensableQuantity;
        const unit = dispensableUnit;
        const currentUser = this.authService.user.getValue();
        const practitionerReference = currentUser?.fhirResourceId
            ? {
                reference: `Practitioner/${currentUser.fhirResourceId}`,
                display: currentUser?.display || currentUser?.name || currentUser?.email,
            }
            : undefined;
        const practitionerDisplay = currentUser?.display || currentUser?.name || currentUser?.email;

        const supplyDeliveryFullUrl = `urn:uuid:${crypto.randomUUID()}`;

        const supplyDelivery: any = {
            resourceType: 'SupplyDelivery',
            meta: {
                tag: [
                    {
                        system: substance.system ?? `${this.backendUrl}/CodeSystem/supply-stock`,
                        code: String(substance.code ?? name).toLowerCase(),
                        display: substance.display ?? name
                    }
                ]
            },
            status: (formData?.status ?? 'completed').toString(),
            occurrenceDateTime: new Date().toISOString(),
            suppliedItem: {
                itemCodeableConcept: {
                    text: name,
                    coding: [
                        {
                            code: substance.code ?? name,
                            display: substance.display ?? name,
                            system: substance.system ?? `${this.backendUrl}/CodeSystem/supply-stock`,
                        },
                    ],
                },
                ...(unit || quantityValue !== undefined
                    ? {
                        quantity: {
                            ...(quantityValue !== undefined && !Number.isNaN(quantityValue) ? { value: quantityValue } : {}),
                            ...(unit ? { unit } : {}),
                        },
                    }
                    : {}),
            },
        };

        // Extensions to track stock and packaging details
        const extensions: any[] = [];

        if (quantityValue !== undefined || unit) {
            extensions.push({
                url: this.supplyDeliveryExtensionUrls.totalAmount,
                valueQuantity: {
                    ...(quantityValue !== undefined ? { value: quantityValue } : {}),
                    ...(unit ? { unit } : {}),
                },
            });
        }

        if (quantityRemaining !== undefined || unit) {
            extensions.push({
                url: this.supplyDeliveryExtensionUrls.totalRemaining,
                valueQuantity: {
                    ...(quantityRemaining !== undefined ? { value: quantityRemaining } : {}),
                    ...(unit ? { unit } : {}),
                },
            });
        }

        if (packagesRemaining !== undefined) {
            extensions.push({
                url: this.supplyDeliveryExtensionUrls.packItemRemaining,
                valueInteger: packagesRemaining,
            });
        }

        if (totalCostPrice !== undefined) {
            extensions.push({
                url: this.supplyDeliveryExtensionUrls.totalCostPrice,
                valueMoney: {
                    value: totalCostPrice,
                    currency: 'NGN',
                },
            });
        }

        if (lowStockThreshold !== undefined) {
            extensions.push({
                url: this.supplyDeliveryExtensionUrls.lowStockThreshold,
                valueQuantity: {
                    value: lowStockThreshold,
                    ...(unit ? { unit } : {}),
                },
            });
        }

        if (lowStockAlertsEnabled !== undefined) {
            extensions.push({
                url: this.supplyDeliveryExtensionUrls.lowStockAlertsEnabled,
                valueBoolean: lowStockAlertsEnabled,
            });
        }

        if (packageName || packageSize !== undefined || packagesReceived !== undefined) {
            extensions.push({
                url: this.supplyDeliveryExtensionUrls.packageDetails,
                extension: [
                    ...(packageName ? [{ url: 'name', valueString: packageName }] : []),
                    ...(packagesReceived !== undefined
                        ? [{
                            url: 'packageCount',
                            valueQuantity: {
                                value: packagesReceived,
                                unit: packageName || 'package',
                            },
                        }]
                        : []),
                    ...(packageSize !== undefined
                        ? [{
                            url: 'quantityPerPackage',
                            valueQuantity: {
                                value: packageSize,
                                ...(unit ? { unit } : {}),
                            },
                        }]
                        : []),
                ],
            });
        }

        const totalRemainingAfter = quantityRemaining ?? quantityValue;
        const totalRemainingBefore = 0;
        if (totalRemainingAfter !== undefined) {
            const changeExtensionChildren: any[] = [
                { url: 'dateChanged', valueDateTime: new Date().toISOString() },
                practitionerReference
                    ? { url: 'practitionerWhoChanged', valueReference: practitionerReference }
                    : practitionerDisplay
                        ? { url: 'practitionerWhoChanged', valueString: practitionerDisplay }
                        : null,
                {
                    url: 'totalRemainingBeforeChange',
                    valueQuantity: {
                        value: totalRemainingBefore,
                        ...(unit ? { unit } : {}),
                    },
                },
                {
                    url: 'totalRemainingAfterChange',
                    valueQuantity: {
                        value: totalRemainingAfter,
                        ...(unit ? { unit } : {}),
                    },
                },
            ].filter(Boolean);

            extensions.push({
                url: this.supplyDeliveryExtensionUrls.totalRemainingChanges,
                extension: changeExtensionChildren,
            });
        }

        if (extensions.length) {
            supplyDelivery.extension = extensions;
        }

        const chargeDraft: ChargeItemDefinition = this.buildChargeItemDefinitionDraft({
            name,
            substance,
            totalCostPrice,
            currency: 'NGN',
        });

        this.utilityService
            .findActiveDuplicateByServiceCode(chargeDraft)
            .pipe(take(1))
            .subscribe({
                next: (matches: ChargeItemDefinition[]) => {
                    const existing = (matches || [])[0];

                    const chargeToSave = existing
                        ? this.mergeCostPriceIntoCharge(existing, totalCostPrice, 'NGN')
                        : this.buildNewChargeItemDefinitionForSupply(chargeDraft);

                    const entries: NonNullable<Bundle['entry']> = [
                        {
                            fullUrl: supplyDeliveryFullUrl,
                            request: { method: 'POST', url: 'SupplyDelivery' },
                            resource: supplyDelivery as any,
                        } as any,
                        existing?.id
                            ? ({
                                request: { method: 'PUT', url: `ChargeItemDefinition/${existing.id}` },
                                resource: chargeToSave as any,
                            } as any)
                            : ({
                                request: { method: 'POST', url: 'ChargeItemDefinition' },
                                resource: chargeToSave as any,
                            } as any),
                    ];

                    const tx: Bundle = {
                        resourceType: 'Bundle',
                        type: 'transaction',
                        entry: entries,
                    };

                    this.http
                        .post<Bundle>(this.backendUrl, tx, {
                            headers: {
                                'Content-Type': 'application/fhir+json',
                                Accept: 'application/fhir+json',
                                Prefer: 'return=representation',
                            },
                        })
                        .pipe(take(1))
                        .subscribe({
                            next: (response) => {
                                if (response) {
                                    this.stateService.processOrgWideBundleTransaction(response as any);
                                }

                                const createdSupply = (response?.entry || [])
                                    .map((e: any) => e?.resource)
                                    .find((r: any) => r?.resourceType === 'SupplyDelivery') as SupplyDelivery | undefined;
                                if (this.dialogRef) {
                                    console.log(createdSupply);
                                    debugger;
                                    this.dialogRef.close(createdSupply ?? supplyDelivery);
                                }
                            },
                            error: (err) => {
                                console.error('Failed to save SupplyDelivery + ChargeItemDefinition transaction', err);
                                this.errorService.openandCloseError('Failed to save material. Please try again.');
                            },
                        });
                },
                error: (err) => {
                    console.error('Failed to check duplicate ChargeItemDefinition', err);
                    this.errorService.openandCloseError('Failed to validate product pricing. Please try again.');
                },
            });
    }

    private buildChargeItemDefinitionDraft(args: {
        name: string;
        substance: { code?: string; display?: string; system?: string; text?: string };
        totalCostPrice: number;
        currency: string;
    }): ChargeItemDefinition {
        const code = (args.substance.code ?? args.name).toString().trim();
        const display = (args.substance.display ?? args.name).toString().trim();
        const system = (args.substance.system ?? `${this.backendUrl}/CodeSystem/supply-stock`).toString().trim();

        return {
            resourceType: 'ChargeItemDefinition',
            status: 'active',
            title: display.toLowerCase().replace(/\s+/g, '_'),
            description: `Cost price: ${args.currency} ${args.totalCostPrice}`,
            url: `${this.backendUrl}/ChargeItemDefinition/${encodeURIComponent(code)}_${Date.now()}`,
            code: {
                coding: [{ code, display, system }],
                text: display,
            },
            extension: this.buildTotalCostExtension(args.totalCostPrice, args.currency),
        } as any;
    }

    private buildNewChargeItemDefinitionForSupply(base: ChargeItemDefinition): ChargeItemDefinition {
        const next: any = structuredClone(base as any);

        // NOTE(FHIR R4): ChargeItemDefinition.instance only permits references to Medication|Substance|Device.
        // SupplyDelivery is not a valid target type for this element (HAPI-0931).
        // If you need to keep a link back to the SupplyDelivery event, use a ChargeItem resource or a custom extension.
        return next as ChargeItemDefinition;
    }

    private mergeCostPriceIntoCharge(existing: ChargeItemDefinition, totalCostPrice: number, currency: string): ChargeItemDefinition {
        const next: any = structuredClone(existing as any);

        const exts: any[] = Array.isArray(next.extension) ? next.extension : [];
        const withoutTotalCost = exts.filter((e: any) => e?.url !== 'http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost');
        next.extension = [...withoutTotalCost, ...this.buildTotalCostExtension(totalCostPrice, currency)];

        // Keep human-friendly copy too.
        next.description = `Cost price: ${currency} ${totalCostPrice}`;
        if (!next.status) next.status = 'active';
        return next as ChargeItemDefinition;
    }

    private buildTotalCostExtension(totalCostPrice: number, currency: string): any[] {
        return [
            {
                url: 'http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost',
                extension: [
                    {
                        url: 'type',
                        valueCode: 'base',
                    },
                    {
                        url: 'code',
                        valueCodeableConcept: {
                            text: 'cost price component',
                        },
                    },
                    {
                        url: 'amount',
                        valueMoney: {
                            value: Number(totalCostPrice),
                            currency: currency || 'NGN',
                        },
                    },
                ],
            },
        ];
    }

    close(): void {
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }
}
